import { type ReactElement, useEffect, useState } from 'react';
import { AdminLayout } from './layout.tsx';
import { Button, Card } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

interface HealthData {
  active_attempts: number;
  autosave_failure_rate: number;
  r2_latency_p95_ms: number;
  recent_alert_count: number;
}

const cardStyle = {
  border: '1px solid rgba(10,10,10,0.08)',
  borderRadius: '16px',
  padding: '24px',
  background: '#FFFFFF',
} as const;

export function SystemHealthPage(): ReactElement {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sweepResult, setSweepResult] = useState<string | null>(null);
  const [backupFormat, setBackupFormat] = useState<'csv' | 'json' | null>(null);
  const [backupDownloading, setBackupDownloading] = useState(false);

  useEffect(() => {
    void (async () => {
      const res = await apiFetch('/api/admin/system-health', {
        method: 'GET',
        credentials: 'same-origin',
      });
      const body = (await res.json()) as
        | {
            ok: true;
            data: HealthData;
          }
        | { ok: false; error?: { message?: string } };

      if (!body.ok) {
        setError(body.error?.message ?? 'Failed to load system health');
        return;
      }

      setHealth(body.data);
    })();
  }, []);

  async function onTriggerSweep(): Promise<void> {
    const res = await apiFetch('/api/internal/sweep-abandoned', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({}),
    });
    const body = (await res.json()) as
      | { ok: true; data: { abandoned_count: number } }
      | { ok: false; error?: { message?: string } };

    if (!body.ok) {
      setSweepResult(body.error?.message ?? 'Sweep failed');
      return;
    }

    setSweepResult(`Swept ${String(body.data.abandoned_count)} abandoned attempts`);
  }

  return (
    <AdminLayout title="System Health" eyebrow="Admin · Ops">
      {error ? <p style={{ color: '#B91C1C' }}>{error}</p> : null}
      <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <article style={cardStyle}>
          <p style={{ margin: 0, color: '#6B6B6B' }}>Active attempts</p>
          <strong>{health ? String(health.active_attempts) : 'loading…'}</strong>
        </article>
        <article style={cardStyle}>
          <p style={{ margin: 0, color: '#6B6B6B' }}>Autosave failure rate</p>
          <strong>{health ? `${String(health.autosave_failure_rate)}%` : 'loading…'}</strong>
        </article>
        <article style={cardStyle}>
          <p style={{ margin: 0, color: '#6B6B6B' }}>R2 latency P95 (ms)</p>
          <strong>{health ? String(health.r2_latency_p95_ms) : 'loading…'}</strong>
        </article>
        <article style={cardStyle}>
          <p style={{ margin: 0, color: '#6B6B6B' }}>Recent alert count</p>
          <strong>{health ? String(health.recent_alert_count) : 'loading…'}</strong>
        </article>
      </div>

      <div style={{ marginTop: '16px' }}>
        <Button variant="secondary" type="button" onClick={() => void onTriggerSweep()}>
          Trigger Abandon Sweep
        </Button>
        {sweepResult ? <p>{sweepResult}</p> : null}
      </div>

      {/* J-A14: Question Bank Backup (Story 81) */}
      <div style={{ marginTop: '32px' }}>
        <Card variant="default" className="p-6 space-y-4">
          <h2 className="font-sans text-title font-medium">Question Bank Backup</h2>
          <p className="font-sans text-body text-muted">
            Download a full offline copy of the question bank.
          </p>
          {!backupFormat ? (
            <div className="flex gap-3">
              <Button variant="primary" type="button" onClick={() => setBackupFormat('csv')}>
                Trigger Backup (CSV)
              </Button>
              <Button variant="secondary" type="button" onClick={() => setBackupFormat('json')}>
                Trigger Backup (JSON)
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="font-sans text-body text-muted">
                Format: <span className="font-mono text-ink">{backupFormat.toUpperCase()}</span>
              </p>
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  type="button"
                  disabled={backupDownloading}
                  onClick={() => {
                    setBackupDownloading(true);
                    const url = backupFormat === 'csv'
                      ? '/api/admin/questions/export.csv'
                      : '/api/admin/questions/export';
                    window.open(url, '_blank');
                    setBackupDownloading(false);
                    setBackupFormat(null);
                  }}
                >
                  {backupDownloading ? 'Downloading…' : 'Confirm Download'}
                </Button>
                <Button variant="secondary" type="button" onClick={() => setBackupFormat(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
